import { LockOutlined, LoginOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Form, Input } from 'antd';
import SubmitFormButton from 'common/components/submit-form-button.component';
import { AuthType } from 'config/constants/auth.constants';
import { AuthTranscription } from 'config/constants/transcription.constant';
import AuthService from 'config/services/auth.service';
import UserService from 'config/services/user.service';
import { RegisterUserPayload, ResetPasswordPayload } from 'config/types/auth.type';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import CartUtils from 'config/utils/cart.util';
import NavigationUtils from 'config/utils/navigation.util';
import { SnackbarUtilities } from 'config/utils/snackbar.util';
import UserUtils from 'config/utils/user.util';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AnyAction, Dispatch } from 'redux';
import { User } from 'store/models/user.model';
import { AuthSlice } from 'store/slices/auth.slice';
import { UserSlice } from 'store/slices/user.slice';
import './style.scss';

type RegisterFormProps = {
  user?: User;
  isResetPassword?: boolean;
  callBack?: () => void;
};

const RegisterForm: React.FC<RegisterFormProps> = ({ user, isResetPassword = false, callBack }) => {
  const [registerForm] = Form.useForm<RegisterUserPayload>();
  const dispatch: Dispatch<AnyAction> = useDispatch();
  const location: Partial<Location> = useLocation();

  const successRegisterUser = async (authLocation: Partial<Location>) => {
    dispatch(UserSlice.fetchingRefresh(true));
    try {
      const getUserProfileResult = await UserService.getUserProfile();
      dispatch(UserSlice.refreshUserProfile(getUserProfileResult));
      await CartUtils.reconcileCartAfterAuth();
      await AuthIntentUtils.resumeAfterAuth(authLocation);
    } finally {
      dispatch(UserSlice.fetchingRefresh(false));
    }
  };

  const handleRegisterUser = async (registerData: RegisterUserPayload) => {
    const authLocation: Partial<Location> = { pathname: location.pathname, search: location.search };
    dispatch(AuthSlice.fetchingRefresh(true));
    try {
      const registerUserResult = await AuthService.registerUser(registerData);
      const { accessToken, refreshToken } = registerUserResult;
      dispatch(AuthSlice.refreshLoginData({ accessToken, refreshToken, isAuthenticated: Boolean(accessToken) }));
      if (accessToken) await successRegisterUser(authLocation);
    } finally {
      dispatch(AuthSlice.fetchingRefresh(false));
    }
  };

  const handleResetPassword = async (resetPasswordData: ResetPasswordPayload) => {
    dispatch(AuthSlice.fetchingRefresh(true));
    await AuthService.resetPassword(resetPasswordData);
    dispatch(AuthSlice.fetchingRefresh(false));
    SnackbarUtilities.success(AuthTranscription.RESET_PASSWORD_SUCCESSFULL);
    callBack?.();
  };

  return (
    <Form
      form={registerForm}
      name='register-form'
      layout='vertical'
      autoComplete='off'
      initialValues={isResetPassword ? user : {}}
      onFinish={isResetPassword ? handleResetPassword : handleRegisterUser}
    >
      {!isResetPassword && (
        <Form.Item
          name='dni'
          rules={UserUtils.getUserDniFieldRules()}
          validateFirst
          validateTrigger='onBlur'
          getValueFromEvent={(event) => UserUtils.formatDni(event.target.value)}
        >
          <Input placeholder={UserUtils.getDniCopy().placeholder} name='dni' />
        </Form.Item>
      )}
      <Form.Item name='email' rules={UserUtils.emailFieldRules} shouldUpdate validateFirst>
        <Input
          prefix={<UserOutlined className='site-form-item-icon' />}
          placeholder={AuthTranscription.FORM_EMAIL_PLACEHOLDER}
          name='email'
          type='email'
          disabled={isResetPassword}
        />
      </Form.Item>
      <Form.Item name='password' rules={UserUtils.passwordFielRules} shouldUpdate validateFirst>
        <Input.Password
          prefix={<LockOutlined className='site-form-item-icon' />}
          placeholder={AuthTranscription.FORM_PASSWORD_PLACEHOLDER}
          name='password'
          minLength={4}
          maxLength={6}
        />
      </Form.Item>
      <Form.Item name='repeat_password' rules={UserUtils.repeatPasswordFields} dependencies={['password']} validateFirst>
        <Input.Password
          prefix={<LockOutlined className='site-form-item-icon' />}
          placeholder={AuthTranscription.FORM_REGISTER_REPEAT_PASSWORD_TEXT}
          name='repeat_password'
          minLength={4}
          maxLength={6}
        />
      </Form.Item>
      <Form.Item>
        <SubmitFormButton
          form={registerForm}
          text={isResetPassword ? AuthTranscription.FORM_RESET_PASSWORD_TEXT : AuthTranscription.FORM_REGISTER_TEXT}
          icon={<LoginOutlined />}
          skipLiveValidation={isResetPassword ? undefined : UserUtils.DNI_SKIP_LIVE_VALIDATION}
        />
        &nbsp; O{' '}
        <Button type='link' onClick={() => NavigationUtils.handleAuthTypeAction(AuthType.LOGIN, true, null, location)}>
          {AuthTranscription.FORM_LOGIN_TEXT}
        </Button>
      </Form.Item>
    </Form>
  );
};
export default RegisterForm;
